import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiCheck, FiUsers, FiTrendingUp, FiAward, FiClock } = FiIcons;

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: '500+', label: 'Completed Projects', icon: FiCheck },
    { number: '25+', label: 'Years Experience', icon: FiClock },
    { number: '1200+', label: 'Happy Clients', icon: FiUsers },
    { number: '98%', label: 'Success Rate', icon: FiTrendingUp },
  ];

  const services = [
    {
      title: 'Residential Construction',
      description: 'Custom homes and residential projects built to perfection.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop',
    },
    {
      title: 'Commercial Building',
      description: 'Professional commercial construction for businesses.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop',
    },
    {
      title: 'Industrial Projects',
      description: 'Large-scale industrial construction and infrastructure.',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop',
    },
  ];

  return (
    <>
      <Helmet>
        <title>BuildCorp - Professional Construction Services | Expert Builders</title>
        <meta name="description" content="BuildCorp offers professional construction services with 25+ years experience. Residential, commercial, and industrial construction experts." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-navy-900 to-navy-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&h=1080&fit=crop)'
          }}
        ></div>
        
        <div className="relative container mx-auto px-4 py-24 lg:py-32">
          <div className="max-w-3xl">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl lg:text-6xl font-bold mb-6"
            >
              Build A Better World
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl mb-8 text-gray-200"
            >
              Professional construction services with over 25 years of experience. 
              We turn your vision into reality with quality craftsmanship and innovative solutions.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link 
                to="/contact"
                className="bg-primary-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors text-center"
              >
                Get Started Today
              </Link>
              <button className="flex items-center justify-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-navy-900 transition-colors">
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                <span>Watch Video</span>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-3xl font-bold text-navy-900 mb-2">{stat.number}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h2 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl font-bold text-navy-900 mb-6"
              >
                Welcome To Construction Real Solutions
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-gray-600 mb-6"
              >
                We are a leading construction company dedicated to delivering exceptional 
                building solutions. Our team of experienced professionals brings expertise, 
                innovation, and commitment to every project we undertake.
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-3 mb-8"
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>Quality construction with attention to detail</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>Experienced team of professionals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>On-time delivery and budget-friendly solutions</span>
                </div>
              </motion.div>
              <Link 
                to="/about"
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors inline-block"
              >
                Learn More About Us
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="bg-primary-500 text-white p-6 rounded-lg"
              >
                <h3 className="text-2xl font-bold mb-2">25+</h3>
                <p>Years of Experience in Construction Industry</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=300&h=200&fit=crop"
                  alt="Construction worker"
                  className="w-full h-48 object-cover rounded-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=300&h=200&fit=crop"
                  alt="Construction site"
                  className="w-full h-48 object-cover rounded-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-navy-900 text-white p-6 rounded-lg"
              >
                <SafeIcon icon={FiAward} className="w-8 h-8 mb-2" />
                <h3 className="text-lg font-semibold">Award Winning Company</h3>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We offer comprehensive construction services for residential, commercial, and industrial projects.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-navy-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link 
                    to="/services"
                    className="text-primary-500 font-medium hover:text-primary-600 transition-colors"
                  >
                    Learn More →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Commitment Section */}
      <section className="py-16 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1541976590-713941681591?w=600&h=400&fit=crop"
                alt="Project team"
                className="w-full rounded-lg"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">Project Commitment</h2>
              <p className="text-gray-300 mb-6">
                We are committed to delivering exceptional results on every project. 
                Our dedicated team ensures that every detail is perfect and every 
                deadline is met with the highest quality standards.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>Professional project management</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>Quality assurance at every step</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500" />
                  <span>Timely project completion</span>
                </div>
              </div>
              <Link 
                to="/projects"
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors inline-block"
              >
                View Our Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 text-primary-100">
            Contact us today for a free consultation and quote.
          </p>
          <Link 
            to="/contact"
            className="bg-white text-primary-500 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
          >
            Get Free Quote
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;